import { generatePath } from 'react-router-dom'
import type { MutationFunction } from '@tanstack/react-query'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'
import { format } from 'date-fns'
import qs from 'qs'

import type {
  DepartmentResponse,
  DepartmentsResponse,
  OrgStructureResponse,
  DepartmentDetails,
  JobRoleResponse,
  LocationResponse,
  FiltersResponse,
  FilterParams,
  ExportFilter,
  TeamUserDetails,
  BulkUpdateRequest,
  LocationsResponse,
  ImportCSVResponse,
} from '@models/OrgStructure'

import api from '../../api'

import { AccountKeys, OrgStructureKeys } from '../Keys'
import { useGetOne, useCreate, useDelete, usePatch, useGetList } from '../request'
import type { Role } from '@type/common'
import type { InviteUser } from '@models/Team'

const PATHS = {
  orgstructure: 'api/teams/orgstructure',
  // Lightweight, NON-team.view-gated picker data (name/email/roles/source_id)
  // for the user dropdowns. The rich roster + Members page stay on
  // `orgstructure`; only the pickers use this so every role can pick a member.
  userOptions: 'api/teams/user-options',
  invites: 'api/teams/orgstructure/invites',
  user: 'api/teams/users/:id',
  filters: 'api/teams/orgstructure/filters',
  bulkUpdate: 'api/teams/users/bulk',

  departments: 'api/teams/departments',
  departmentById: 'api/teams/departments/:id',
  jobRoles: 'api/teams/departments/:departmentId/job-roles',
  jobRoleById: 'api/teams/departments/:departmentId/job-roles/:id',

  locations: 'api/teams/locations',
  locationById: 'api/teams/locations/:id',

  import: 'api/teams/orgstructure/import',
  export: 'api/teams/orgstructure/export',
  slackImport: 'api/teams/users/import/slack',
  microsoftImport: 'api/teams/users/import/microsoft',
  bambooHRImport: 'api/teams/users/import/bamboo',

  taskStatus: 'task-status',
}

export const useGetTeamOrgStructure = (filter?: FilterParams, enabled = true) => {
  return useGetOne<OrgStructureResponse>([OrgStructureKeys.OrgStructure, filter], PATHS.orgstructure, {
    query: {
      cacheTime: 5 * 60 * 1000, // 5 min: discard inactive filter combos
      staleTime: 30 * 1000, // 30 s: refetch when revisiting a filter
      enabled,
    },
    axios: {
      params: { ...(filter || {}) },
    },
  })
}
export const useGetTeamDropdownList = (filter?: FilterParams, enabled = true) => {
  // The query key must vary by search/size, otherwise every distinct search
  // overwrites the same cache slot and the Infinity cache/stale times do
  // nothing — repeating a past search would still hit the network.
  return useGetOne<OrgStructureResponse>(
    teamDropdownQueryKey(filter?.search ?? '', filter?.size ?? 100),
    PATHS.userOptions,
    {
      query: {
        cacheTime: Infinity,
        staleTime: Infinity,
        enabled,
      },
      axios: {
        params: { ...(filter || {}) },
      },
    },
  )
}

// Shared so an imperative `queryClient.fetchQuery` (e.g. the async user picker,
// which must fetch the exact query string react-select hands it rather than a
// state-bound refetch) hits the same cache slot as the hook above.
export const teamDropdownQueryKey = (search = '', size = 100) =>
  [OrgStructureKeys.OrgStructureDropdown, search, size] as const

export const fetchTeamDropdownList = (search = '', size = 100) =>
  api.get<OrgStructureResponse>(PATHS.userOptions, { params: { search, size } }).then((res) => res.data)

type UserFilters = { ids: number[]; key?: string; size?: number; search?: string }
export const useGetUsersByIds = (filter?: UserFilters) => {
  const ids = filter?.ids?.filter((el) => typeof el === 'number')
  const sortedIdsKey = [...(ids || [])].sort((a, b) => a - b).join(',')

  const queryKey: Array<string | number | undefined> = [
    OrgStructureKeys.OrgStructureDropdownById,
    sortedIdsKey,
    filter?.search,
    filter?.size,
  ]
  if (filter?.key) {
    queryKey.push(filter?.key)
  }
  return useQuery<OrgStructureResponse>(
    queryKey,
    async () => {
      if (ids?.length) {
        const response = await api.get<OrgStructureResponse>(PATHS.userOptions, {
          params: { ids, size: filter?.size || filter?.ids?.length || 0, search: filter?.search },
          paramsSerializer: (param) => {
            return qs.stringify(param, { arrayFormat: 'repeat' })
          },
        })
        return response.data
      }
      return { count: 0, results: [] }
    },
    {
      enabled: !!ids?.length,
      staleTime: 30 * 1000,
      placeholderData: {
        results: [],
        count: 0,
      },
    },
  )
}
export const useGetTeamInvites = () => {
  return useGetList<InviteUser>([OrgStructureKeys.Invites], PATHS.invites, {
    query: {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })
}

// Revoke a pending invite. The backend matches by UUID (not email),
// so the row's `uuid` field is the source of truth — passing email
// would race against re-invites that recycle the address. Invalidates
// both the Invites list AND the OrgStructure list — the Members table
// is backed by OrgStructure (carries `is_invited` + `invite_uuid` per
// row), so revoking has to refresh that cache or the "Invite sent"
// chip lingers until a manual reload.
// Require a UUID at the type level (like useResendTeamInvite): the old
// `uuid?` fell back to DELETE /api/teams/invites/ (the bulk-create
// collection endpoint) — a completely different contract.
export const useRevokeTeamInvite = (uuid: string) => {
  const cache = useQueryClient()
  return useDelete(`/api/teams/invites/${uuid}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.Invites])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      },
    },
  })
}

// Re-send a pending invite. Backend matches by UUID; calling PATCH on
// an existing invite refreshes the expiry, rewrites the role list
// (when provided), and re-emails the link. The body is intentionally
// empty here — that path bare-resends without changing anything.
// Require a UUID at the type level: previously this fell back to
// ``/api/teams/invites/`` (bulk create endpoint) when uuid was
// undefined, which is a completely different mutation contract and
// would silently send the wrong request shape.
export const useResendTeamInvite = (uuid: string) => {
  const cache = useQueryClient()
  return usePatch<Record<string, never>>(`/api/teams/invites/${uuid}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.Invites])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      },
    },
  })
}

export const useGetOrgStructureFilters = () => {
  return useGetOne<FiltersResponse>([OrgStructureKeys.Filters], PATHS.filters)
}

export const useGetTeamDepartments = (enabled = true, notEmpty = false) => {
  return useGetOne<DepartmentsResponse>([OrgStructureKeys.TeamDepartments, notEmpty], PATHS.departments, {
    axios: {
      params: {
        limit: 0,
        not_empty: notEmpty ? 1 : undefined,
      },
    },
    query: {
      enabled,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })
}

export const useAddTeamDepartment = () => {
  const cache = useQueryClient()
  return useCreate<{ name: string }, DepartmentResponse>(PATHS.departments, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.TeamDepartments])
      },
    },
  })
}

export const useEditTeamDepartment = (id?: number) => {
  const cache = useQueryClient()
  return usePatch<{ name: string }, DepartmentResponse>(
    id ? PATHS.departmentById.replace(':id', id.toString()) : PATHS.departmentById,
    {
      mutation: {
        onSuccess: () => {
          cache.invalidateQueries([OrgStructureKeys.TeamDepartments])
          cache.invalidateQueries([OrgStructureKeys.Department, +(id || 0)])
        },
      },
    },
  )
}

export const useDeleteDepartment = (id?: number) => {
  const cache = useQueryClient()
  return useDelete(id ? PATHS.departmentById.replace(':id', id.toString()) : PATHS.departmentById, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.TeamDepartments])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      },
    },
  })
}

export const useGetDepartmentDetails = (id?: number | string | null) => {
  return useGetOne<DepartmentDetails>([OrgStructureKeys.Department, +(id || 0)], `${PATHS.departments}/${id}`, {
    query: {
      enabled: !!id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })
}

export const useAddDepartmentJobRole = (depId?: number) => {
  const cache = useQueryClient()
  return useCreate<{ name: string } | string[], JobRoleResponse | JobRoleResponse[]>(
    depId ? PATHS.jobRoles.replace(':departmentId', depId?.toString()) : PATHS.jobRoles,
    {
      mutation: {
        onSuccess: () => {
          cache.invalidateQueries([OrgStructureKeys.Department, depId])
        },
      },
    },
  )
}

export const useEditDepartmentJobRole = (depId?: number, id?: number) => {
  const cache = useQueryClient()
  const path =
    id && depId
      ? PATHS.jobRoleById.replace(':departmentId', depId.toString()).replace(':id', id.toString())
      : PATHS.jobRoleById
  return usePatch<{ name: string }, JobRoleResponse>(path, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.Department, depId])
      },
    },
  })
}

export const useDeleteDepartmentJobRole = (depId?: number, id?: number) => {
  const path =
    id && depId
      ? PATHS.jobRoleById.replace(':departmentId', depId.toString()).replace(':id', id.toString())
      : PATHS.jobRoleById
  return useDelete(path, {})
}

export const useGetTeamLocations = (enabled = true) => {
  return useGetOne<LocationsResponse>([OrgStructureKeys.Locations], `${PATHS.locations}`, {
    axios: {
      params: {
        limit: 0,
      },
    },
    query: {
      enabled,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })
}

export const useGetLocationDetails = (id?: number | string | null) => {
  return useGetOne<DepartmentDetails>([OrgStructureKeys.Location, +(id || 0)], `${PATHS.locations}/${id}`, {
    query: {
      enabled: !!id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })
}

export const useAddLocation = () => {
  const cache = useQueryClient()
  return useCreate<{ name: string }, LocationResponse>(PATHS.locations, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.Locations])
      },
    },
  })
}

export const useEditLocation = (id?: number) => {
  const cache = useQueryClient()
  const path = id ? PATHS.locationById.replace(':id', id.toString()) : PATHS.locationById
  return usePatch<{ name: string }, LocationResponse>(path, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.Locations])
        cache.invalidateQueries([OrgStructureKeys.Location, +(id || 0)])
      },
    },
  })
}

export const useDeleteLocation = (id?: number) => {
  const cache = useQueryClient()
  const path = id ? PATHS.locationById.replace(':id', id.toString()) : PATHS.locationById
  return useDelete(path, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.Locations])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      },
    },
  })
}

export const useExportOrgStructure = (params: ExportFilter) => {
  return useMutation(
    async () => {
      const response = await api.get(PATHS.export, {
        params: Object.keys(params).reduce(
          (acc, key) =>
            params[key as keyof ExportFilter] ? { ...acc, [key]: params[key as keyof ExportFilter] } : acc,
          {},
        ),
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `org_structure_${format(new Date(), 'yyyy-MM-dd-hh-mm')}.${params.type}`)
      document.body.appendChild(link)
      link.click()

      return null
    },
    {
      mutationKey: [OrgStructureKeys.Export],
    },
  )
}

export const useImportOrgStructureCsv = () => {
  const cache = useQueryClient()
  const fileUpload: MutationFunction<ImportCSVResponse, { file: File; role?: Role; role_id?: number }> = async ({
    file,
    role,
    role_id,
  }) => {
    const form = new FormData()
    form.append('file', file)
    if (role) {
      form.append('role', role)
    }
    // RBAC role assigned to every imported user (custom or system). The
    // backend validates it belongs to the team and applies it via the
    // invite's role set. `role` above stays for the legacy create path.
    if (role_id !== undefined) {
      form.append('role_id', String(role_id))
    }

    const res: AxiosResponse<ImportCSVResponse> = await api.post(PATHS.import, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res.data
  }

  return useMutation(fileUpload, {
    onSuccess: () => {
      cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      cache.invalidateQueries([OrgStructureKeys.Filters])
    },
  })
}

export const useImportUsersFromSlack = () => {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: async ({ role, role_id }: { role?: Role; role_id?: number }) => {
      return (await api.get(PATHS.slackImport, { params: { role, role_id } })).data
    },
    mutationKey: [OrgStructureKeys.SlackImport],
    onSuccess: () => {
      cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      cache.invalidateQueries([OrgStructureKeys.Filters])
    },
  })
}

export const useImportUsersFromMicrosoft = () => {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: async ({ role, role_id }: { role?: Role; role_id?: number }) => {
      return (await api.get(PATHS.microsoftImport, { params: { role, role_id } })).data
    },
    mutationKey: [OrgStructureKeys.MicrosoftImport],
    onSuccess: () => {
      cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      cache.invalidateQueries([OrgStructureKeys.Filters])
    },
  })
}

export const useImportFromBambooHR = () => {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: async ({ role, role_id }: { role?: Role; role_id?: number }) => {
      return (await api.get(PATHS.bambooHRImport, { params: { role, role_id } })).data
    },
    mutationKey: [OrgStructureKeys.BambooHRImport],
    onSuccess: () => {
      cache.invalidateQueries([OrgStructureKeys.OrgStructure])
      cache.invalidateQueries([OrgStructureKeys.Filters])
    },
  })
}

export const useGetTeamUser = (id?: number | null) => {
  return useGetOne<TeamUserDetails>(
    [OrgStructureKeys.OrgStructure, id],
    id ? generatePath(PATHS.user, { id }) : PATHS.user,
    {
      query: {
        enabled: !!id,
      },
    },
  )
}

export const useFetchTeamUser = () => {
  return useMutation<TeamUserDetails, unknown, number>(async (id: number) => {
    const { data } = await api.get<TeamUserDetails>(generatePath(PATHS.user, { id }))
    return data
  })
}

export const useFetchUserOrgData = () => {
  return useMutation<OrgStructureResponse, unknown, number>(async (userId: number) => {
    const { data } = await api.get<OrgStructureResponse>(PATHS.orgstructure, {
      params: { ids: [userId], size: 1 },
      paramsSerializer: (param) => qs.stringify(param, { arrayFormat: 'repeat' }),
    })
    return data
  })
}

export const useBulkUpdate = () => {
  const cache = useQueryClient()
  return usePatch<BulkUpdateRequest>(PATHS.bulkUpdate, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
        cache.invalidateQueries([AccountKeys.Me])
      },
    },
  })
}

export const useGetTaskStatus = () => {
  return useMutation(async (taskId: string | number) => {
    const { data: isTaskCompleted } = await api.get(`${PATHS.taskStatus}/${taskId}`)
    return isTaskCompleted
  })
}
